import React from 'react';
import i18n from 'i18next';
import { useHistory } from 'react-router-dom';
import { Status, Button, Icon, CardRow, Chip, Date, Dropdown, DropdownItem, Numbers, ButtonDropdown, InfoText, Modal, Tooltip, ToastContext } from '@weezevent/nacre';
import { Trans, useTranslation } from 'react-i18next';
import EventModel from '../../../models/eventModel';
import { SendMailAccountActivationModal } from '../../../components/template/contextMenu/event/sendMailAccountActivationModal';
import PlaceholderImage from '../../../images/placeholder-image.png';
import EventGraph from './components/EventGraph';
import Style from './eventCard.module.css';

function EventImage({ event }) {
    return (
        <div
            className={Style['event-card-banner-container']}
            style={{
                backgroundImage: `url(${event.full_picture || PlaceholderImage})`
            }}
        />
    );
}

function EventName({ event }) {
    return (
        <div className={Style['event-card-title']}>
            <div>{event.name}</div>
        </div>
    );
}

function EventChipStatus({ event }) {
    const { t } = useTranslation();

    const timeStatus = React.useMemo(() => {
        return event.get_time_status();
    }, [event]);

    return (
        <div className={Style['event-chip']}>
            <Chip text={t(`eventList.${timeStatus}`)} status={timeStatus} />
        </div>
    );
}

function EventAddress({ event }) {
    return (
        <div className={Style['event-card-location']}>
            {(event.address?.city || event.address?.name || event.subtype === 'events') && (
                <>
                    <Icon name="map" /> {event.subtype === 'events' && <Trans i18nKey="eventList.online" />}
                    {event.subtype !== 'events' && event.address.name && event.address.name}
                    {event.address?.name && event.address.city && ', '}
                    {event.address?.city && event.address.city}
                </>
            )}
        </div>
    );
}

function EventDate({ event }) {
    const getDayFromDate = (dateString, timezone) => {
        let date = new Date(dateString);
        date = date.toLocaleDateString(i18n.language, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: timezone
        });
        return date;
    };

    return (
        <div className={Style['event-card-calendar']}>
            <Icon name="calendar" /> {getDayFromDate(event.start_date, event.timezone)}
            <EventChipStatus event={event} />
        </div>
    );
}

function EventTypeTag({ condition, icon, tagKey, tooltipKey, tooltipStyleClass, chipStyleClass }) {
    const { t } = useTranslation();
    return (
        condition && (
            <Tooltip className={tooltipStyleClass} triggerOnHover title={t(tooltipKey)}>
                <Chip className={chipStyleClass} icon={icon} text={t(tagKey)} />
            </Tooltip>
        )
    );
}

function EventTags({ event }) {
    return (
        <div className={Style['event-tags']}>
            <span className={Style['event-card-tags']}>
                <EventTypeTag
                    condition={event.is_seated}
                    icon="seat"
                    tagKey="eventList.tag-seated"
                    tooltipKey="eventList.tooltip-seated"
                    chipStyleClass={`${Style['event-tag']} ${Style['event-type-tag']}`}
                    tooltipStyleClass={Style['tooltip-tag']}
                />
                <EventTypeTag
                    condition={event.use_sessions}
                    icon="calendar"
                    tagKey="eventList.tag-sessions"
                    tooltipKey="eventList.tooltip-sessions"
                    chipStyleClass={`${Style['event-tag']} ${Style['event-type-tag']}`}
                    tooltipStyleClass={Style['tooltip-tag']}
                />
                <EventTypeTag
                    condition={event.is_member}
                    icon="id-card"
                    tagKey="eventList.tag-subscription"
                    tooltipKey="eventList.tooltip-subscription"
                    chipStyleClass={`${Style['event-tag']} ${Style['event-subscription-type-tag']}`}
                    tooltipStyleClass={Style['tooltip-tag']}
                />
                {event.tags?.length > 0 &&
                    event.tags.map((tag, index) => <Chip key={index} className={`${Style['event-tag']} ${Style['event-basic-tag']}`} text={`#${tag.label}`} />)}
            </span>
        </div>
    );
}

function EventStatus({ event, organization, sendMailAccountActivationModal, setSendMailAccountActivationModal, resetPagination, organizationInfos, onClick }) {
    const currentStatus = event.publication_status;
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [selectedStatus, setSelectedStatus] = React.useState(event.publication_status);
    const [openModal, setOpenModal] = React.useState(false);

    const onCloseModal = React.useCallback(() => {
        setOpenModal(false);
        setSelectedStatus(currentStatus);
    }, [currentStatus]);

    const submitStatus = React.useCallback(
        status => {
            let eventObject = new EventModel({
                id: event.id,
                publication_status: status
            });
            eventObject
                .save({ organizationId: organization.id })
                .then(() => {
                    onCloseModal();
                    resetPagination();
                    toast.success(t('eventList.status.toast-success'));
                })
                .catch(e => {
                    console.log(e);
                    toast.error(t('eventList.status.toast-error'));
                });
        },
        [event, organization, resetPagination]
    );

    const changeStatus = React.useCallback(
        value => {
            if (value === 'closed') {
                setOpenModal(true);
            }
            if (organizationInfos.can_publish_errors.includes('account_not_validated') && currentStatus === 'draft' && value === 'published') {
                setSendMailAccountActivationModal(true);
            }
            if (value === 'published' || value === 'draft') {
                submitStatus(value);
            }
            setSelectedStatus(value);
        },
        [setSelectedStatus, organizationInfos, setOpenModal, currentStatus, submitStatus]
    );

    const status = React.useMemo(() => {
        if (currentStatus === 'published') {
            return { color: 'green', status: 'published', text: t('eventList.status.published') };
        }
        if (currentStatus === 'draft') {
            return { color: 'orange', status: 'draft', text: t('eventList.status.draft') };
        }
    }, [currentStatus]);

    const items = [
        currentStatus === 'draft' && <DropdownItem key={1} item={t('eventList.status.published')} onClick={() => changeStatus('published')} />,
        currentStatus === 'published' && <DropdownItem key={2} item={t('eventList.status.draft')} onClick={() => changeStatus('draft')} />,
        (currentStatus === 'published' || currentStatus === 'draft') && <DropdownItem key={3} item={t('eventList.status.closed')} onClick={() => changeStatus('closed')} />
    ];

    return (
        <div className={Style['status-container']} onClick={onClick}>
            <SendMailAccountActivationModal open={sendMailAccountActivationModal} onSubmit={() => {}} onClose={() => setSendMailAccountActivationModal(false)} />
            <Modal
                allowExternalClosing={false}
                open={openModal && selectedStatus === 'closed'}
                onClose={onCloseModal}
                submitButton={<Button primary label={t('ticket.common.action.confirm')} onClick={() => submitStatus('closed')} />}
                cancelButton={<Button onClick={onCloseModal} label={t(`ticket.common.action.back`)} />}
                title={t('eventList.status.closedStatusModal.title')}
            >
                <div style={{ marginTop: '15px' }}>{t('eventList.status.closedStatusModal.description')}</div>
            </Modal>
            {currentStatus !== 'closed' ? (
                <Status.Dropdown text={status.text} value={status.status} color={status.color} items={items} />
            ) : (
                <Status text={t('eventList.status.closed')} color="red" />
            )}
        </div>
    );
}

export default function EventCard({ organization, event, resetPagination, organizationInfos, onChangeSelected, onChangeActionModal, className = null }) {
    const { t } = useTranslation();
    const history = useHistory();
    const locale = i18n.language;

    const [sendMailAccountActivationModal, setSendMailAccountActivationModal] = React.useState(false);

    const eventGlobalQuota = React.useMemo(() => {
        const setting = event.settings?.find(set => set.usage === 'evenement_billeterie');
        return {
            hasQuota: setting ? setting.values['limitation_places'] === '1' : false,
            quota: setting ? setting.values['nombre_billets'] : '0'
        };
    }, [event]);

    return (
        <CardRow className={`${Style['event-card-content']} ${className}`} as="a" href={`/ticket/O${organization.id}/E${event.id}/dashboard`}>
            <CardRow.LeftComponent>
                <div className={Style['event-card-infos']}>
                    <EventImage event={event} />
                </div>
            </CardRow.LeftComponent>
            <CardRow.RightComponent>
                <>
                    <div className={Style['event-card-title-container']}>
                        <EventName event={event} />
                    </div>
                    <div className={Style['event-card-infos-container']}>
                        <div className={Style['event-row-container']}>
                            <div className={Style['event-row-infos']}>
                                <EventAddress event={event} />
                                <EventDate event={event} />
                                <div className={Style['event-id']}>E {event.id}</div>
                                <EventTags event={event} />
                            </div>
                            <div className={`${Style['event-card-actions-buttons']} ${className ? `${className}_tools` : ''}`}>
                                {eventGlobalQuota.hasQuota ? (
                                    <EventGraph
                                        locale={locale}
                                        count={event.attendees}
                                        quota={eventGlobalQuota.quota}
                                        subtitle={t('ticket.common.attendee', { count: event.attendees })}
                                    />
                                ) : (
                                    <InfoText subtitle={t('ticket.common.attendee', { count: event.attendees || 0 })}>
                                        <span className={Style['total-attendees']}>
                                            <Numbers value={event.attendees || 0} locale={locale} />
                                        </span>
                                    </InfoText>
                                )}

                                <EventStatus
                                    event={event}
                                    onClick={e => e.preventDefault()}
                                    organization={organization}
                                    sendMailAccountActivationModal={sendMailAccountActivationModal}
                                    setSendMailAccountActivationModal={setSendMailAccountActivationModal}
                                    organizationInfos={organizationInfos}
                                    resetPagination={resetPagination}
                                />
                                <div className={Style['icon-button-container']} onClick={e => e.preventDefault()}>
                                    <Tooltip triggerOnHover title={t('eventList.dropdown.attendees')}>
                                        <CardRow.IconButton
                                            onClick={() => window.open(`/ticket/O${organization.id}/E${event.id}/manager/attendees`, '_blank')}
                                            icon={<Icon name="group" margin={false} />}
                                        />
                                    </Tooltip>
                                    <Tooltip triggerOnHover title={t('eventList.dropdown.stats')}>
                                        <CardRow.IconButton
                                            onClick={() => window.open(`/ticket/O${organization.id}/E${event.id}`, '_blank')}
                                            className={Style['icon-button-stats']}
                                            icon={<Icon name="Piechart" margin={false} />}
                                        />
                                    </Tooltip>
                                    <Tooltip triggerOnHover title={t('eventList.dropdown.minisite')}>
                                        <CardRow.IconButton
                                            onClick={() =>
                                                window.open(
                                                    event.status === 'draft' ? `/ticket/O${organization.id}/E${event.id}/channels/page` : event.gemino_url || event.page_url,
                                                    '_blank'
                                                )
                                            }
                                            icon={<Icon name="globe" margin={false} />}
                                        />
                                    </Tooltip>
                                </div>
                                <ButtonDropdown
                                    onClick={e => e.preventDefault()}
                                    renderWhenClosed
                                    trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                                    items={[
                                        <div key="items" onClick={e => e.preventDefault()}>
                                            <div className={Style['dropdown-button-items']} key="actions">
                                                <DropdownItem
                                                    key="attendees"
                                                    item={t('eventList.dropdown.attendees')}
                                                    onClick={() => {
                                                        history.push(`/ticket/O${organization.id}/E${event.id}/manager/attendees`);
                                                    }}
                                                />
                                                <DropdownItem
                                                    key="stats"
                                                    item={t('eventList.dropdown.stats')}
                                                    onClick={() => {
                                                        history.push(`/ticket/O${organization.id}/E${event.id}/dashboard`);
                                                    }}
                                                />
                                                <DropdownItem
                                                    key="minisite"
                                                    item={t('eventList.dropdown.minisite')}
                                                    onClick={() => {
                                                        window.open(event.gemino_url || event.page_url, '_blank');
                                                    }}
                                                />
                                            </div>
                                            <DropdownItem
                                                key="duplicate"
                                                item={t('ticket.common.action.duplicate')}
                                                onClick={() => {
                                                    onChangeActionModal('duplicate');
                                                    onChangeSelected(event);
                                                }}
                                            />
                                            <DropdownItem
                                                disabled
                                                key="tag"
                                                item={t('eventList.dropdown.addTag')}
                                                onClick={() => {
                                                    onChangeActionModal('tag');
                                                    onChangeSelected(event);
                                                }}
                                            />
                                            <Dropdown.Line />
                                            <DropdownItem
                                                deleteItem
                                                key="delete"
                                                item={t('ticket.common.action.delete')}
                                                onClick={() => {
                                                    onChangeActionModal('delete');
                                                    onChangeSelected(event);
                                                }}
                                            />
                                        </div>
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </>
            </CardRow.RightComponent>
        </CardRow>
    );
}
